/*
 * Purpose: AutoBlock Flow Setup — per-flow secrets, OAuth, and connector readiness
 * Last modified: 2026-06-27 | Agent
 * Completeness: 90
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle2,
  KeyRound,
  Link2,
  Loader2,
  ShieldCheck,
  XCircle,
} from 'lucide-react';
import type { OutcomePlan } from '@/services/autoblocks/types';
import { auditPlanReadiness } from '@/services/autoblocks/planReadiness';
import { useSecretStore } from '@/stores/secretStore';
import {
  connectGmailOAuth,
  getGmailAccessToken,
  hasGoogleOAuthConfig,
} from '@/services/connectors/gmailOAuth';
import { createGmailInbox, checkWebhookServerHealth } from '@/services/connectors/gmailInbound';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { toast } from 'sonner';

interface PlanReadinessPanelProps {
  plan: OutcomePlan;
  onPlanUpdate: (plan: OutcomePlan) => void;
}

export function PlanReadinessPanel({ plan, onPlanUpdate }: PlanReadinessPanelProps) {
  const secrets = useSecretStore((s) => s.secrets);
  const isUnlocked = useSecretStore((s) => s.isUnlocked);
  const hasEncryptedVault = useSecretStore((s) => s.hasEncryptedVault);
  const setSecret = useSecretStore((s) => s.setSecret);

  const report = useMemo(() => auditPlanReadiness(plan), [plan, secrets, isUnlocked]);

  const [draftSecrets, setDraftSecrets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  if (report.items.length === 0) return null;

  const pct =
    report.requiredTotal > 0
      ? Math.round((report.requiredReady / report.requiredTotal) * 100)
      : 100;

  const saveSecret = async (name: string) => {
    const val = draftSecrets[name]?.trim();
    if (!val) {
      toast.error('Enter a value first');
      return;
    }
    if (!isUnlocked) {
      toast.error(
        hasEncryptedVault
          ? 'Unlock secrets vault first — Builder → Secrets'
          : 'Create a secrets vault first — Builder → Secrets'
      );
      return;
    }
    try {
      await setSecret(name, val);
      setDraftSecrets((d) => ({ ...d, [name]: '' }));
      toast.success(`${name} saved to global vault`);
      onPlanUpdate(plan);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Save failed');
    }
  };

  const onConnectGmail = async () => {
    setBusy('gmail');
    try {
      await connectGmailOAuth();
      toast.success('Gmail connected');
      onPlanUpdate(plan);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Gmail OAuth failed');
    } finally {
      setBusy(null);
    }
  };

  const onCreateInbox = async () => {
    setBusy('inbox');
    try {
      const up = await checkWebhookServerHealth();
      if (!up) throw new Error('Inbound API offline — run npm run dev or deploy Worker');
      const inbox = await createGmailInbox();
      onPlanUpdate({
        ...plan,
        gmailInboxId: inbox.inboxId,
        gmailPostUrl: inbox.postUrl,
        useGmailInbound: true,
      });
      toast.success('Edge inbox created');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Inbox creation failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card
      className={
        report.allRequiredReady
          ? 'border-green-500/40 bg-green-500/5'
          : 'border-amber-500/40 bg-amber-500/5'
      }
    >
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center gap-2">
          <ShieldCheck className="h-4 w-4 ui-icon" />
          <CardTitle className="text-base">{PRODUCT_TERMS.autoBlockFlowSetup}</CardTitle>
          <Badge variant={report.allRequiredReady ? 'default' : 'secondary'}>
            {report.requiredReady}/{report.requiredTotal} required
          </Badge>
        </div>
        <CardDescription className="text-xs">
          Setup for this {PRODUCT_TERMS.autoBlockFlow} only — checks vault secrets and connections
          these {PRODUCT_TERMS.blocks.toLowerCase()} need. Dry run still works without these; live
          runs need them complete.{' '}
          {!isUnlocked && (
            <span className="text-amber-600 dark:text-amber-400">
              {hasEncryptedVault
                ? 'Vault locked — unlock in Builder → Secrets.'
                : 'No vault yet — create one in Builder → Secrets.'}
            </span>
          )}
        </CardDescription>
        {report.requiredTotal > 0 && (
          <Progress value={pct} className="h-1.5 mt-2" />
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <ul className="space-y-2">
          {report.items.map((item) => {
            const isReady = item.status === 'ready';
            return (
              <li
                key={item.id}
                className="rounded-md border bg-background/80 p-3 text-sm space-y-2"
              >
                <div className="flex items-start gap-2">
                  {isReady ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{item.label}</span>
                      {!item.required && (
                        <Badge variant="outline" className="text-[10px]">
                          optional
                        </Badge>
                      )}
                      <Badge
                        variant={isReady ? 'default' : 'secondary'}
                        className="text-[10px]"
                      >
                        {isReady ? 'ready' : 'needed'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.description}</p>
                  </div>
                </div>

                {!isReady && item.kind === 'secret' && item.secretName && (
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end pt-1">
                    <div className="flex-1 min-w-0">
                      <Label className="text-xs">{item.secretName}</Label>
                      <Input
                        type="password"
                        placeholder="Paste key…"
                        className="mt-1 text-xs font-mono"
                        value={draftSecrets[item.secretName] ?? ''}
                        onChange={(e) =>
                          setDraftSecrets((d) => ({ ...d, [item.secretName!]: e.target.value }))
                        }
                      />
                    </div>
                    <Button
                      size="sm"
                      className="w-full sm:w-auto shrink-0"
                      onClick={() => saveSecret(item.secretName!)}
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-1" />
                      Save to Secrets
                    </Button>
                  </div>
                )}

                {!isReady && item.id === 'gmail_access' && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={() => void onConnectGmail()}
                      disabled={busy !== null || !hasGoogleOAuthConfig()}
                    >
                      {busy === 'gmail' ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : (
                        <Link2 className="h-3.5 w-3.5 mr-1" />
                      )}
                      Connect Gmail
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => void onCreateInbox()}
                      disabled={busy !== null}
                    >
                      {busy === 'inbox' ? (
                        <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      ) : null}
                      Create edge inbox
                    </Button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        {report.allRequiredReady && (
          <p className="text-xs text-green-700 dark:text-green-400 flex items-center gap-2">
            <CheckCircle2 className="h-3.5 w-3.5" />
            All required credentials are set — you can dry run and approve for the builder.
          </p>
        )}

        {getGmailAccessToken() && (
          <p className="text-[10px] text-muted-foreground">
            Gmail token stored as GOOGLE_ACCESS_TOKEN (expires ~1h; reconnect before live runs).
          </p>
        )}
      </CardContent>
    </Card>
  );
}
